import { ValidatorFn } from '@angular/forms';
import { CredentialSchema } from '../interfaces/collection.interface';
import {
  BRAZILIAN_TAX_ID_SHAPE_VALIDATOR,
  classifyBrazilianTaxId,
  formatCpfOrCnpjProgressively,
  isEmail,
  onlyDigits,
} from './credential-identifiers';

export type CredentialSemantic =
  | 'text'
  | 'password'
  | 'username'
  | 'email'
  | 'cpf'
  | 'cnpj'
  | 'login_identifier'
  | 'brazilian_tax_id';

export interface ResolvedCredentialField {
  controlName: string;
  sourceFields: string[];
  semantic: CredentialSemantic;
  required: boolean;
  inputType: 'text' | 'email' | 'password';
  labelKey?: string;
  placeholderKey?: string;
  errorKey?: string;
  order: number;
  validators: ValidatorFn[];
  formatOnInput?: (raw: string) => string;
  toPayload: (raw: string) => Record<string, string>;
}

export interface ResolvedCredentialOneOf {
  type: 'collapsed' | 'separate';
  options: string[][];
  controlNames: string[];
}

export interface ResolvedCredentialSchema {
  fields: ResolvedCredentialField[];
  baseRequiredSourceFields: string[];
  oneOf?: ResolvedCredentialOneOf;
}

export function resolveCredentialFieldLabel(
  field: ResolvedCredentialField,
  translate: (key: string) => string,
): string {
  if (field.labelKey) {
    return translate(field.labelKey);
  }

  return (
    field.sourceFields[0]
      ?.replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase())
      .trim() || field.controlName
  );
}

interface CollapsibleGroup {
  id: string;
  members: string[];
  controlName: string;
  semantic: CredentialSemantic;
  inputType: 'text' | 'email' | 'password';
  labelKey: string;
  placeholderKey: string;
  errorKey: string;
  validators: ValidatorFn[];
  formatOnInput?: (raw: string) => string;
  resolve: (value: string) => Record<string, string>;
}

const DEFAULT_FIELD_COPY_BY_SEMANTIC: Partial<
  Record<
    CredentialSemantic,
    {
      labelKey: string;
      placeholderKey: string;
      errorKey: string;
    }
  >
> = {
  password: {
    labelKey: 'credentials_password_label',
    placeholderKey: 'credentials_password_placeholder',
    errorKey: 'credentials_password_error',
  },
  username: {
    labelKey: 'credentials_username_label',
    placeholderKey: 'credentials_username_placeholder',
    errorKey: 'credentials_username_error',
  },
};

const PASSWORD_KEYWORDS = ['password', 'senha', 'pass', 'pwd'];

const COLLAPSIBLE_GROUPS: readonly CollapsibleGroup[] = [
  {
    id: 'login_identifier',
    members: ['username', 'email'],
    controlName: 'login_identifier',
    semantic: 'login_identifier',
    inputType: 'text',
    labelKey: 'credentials_username_or_email_label',
    placeholderKey: 'credentials_username_or_email_placeholder',
    errorKey: 'credentials_username_or_email_error',
    validators: [],
    resolve: (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        return {};
      }

      const payload: Record<string, string> = {};

      if (isEmail(trimmed)) {
        payload['email'] = trimmed;
        return payload;
      }

      payload['username'] = trimmed;
      return payload;
    },
  },
  {
    id: 'brazilian_tax_id',
    members: ['cpf', 'cnpj'],
    controlName: 'brazilian_tax_id',
    semantic: 'brazilian_tax_id',
    inputType: 'text',
    labelKey: 'credentials_cpf_or_cnpj_label',
    placeholderKey: 'credentials_cpf_or_cnpj_placeholder',
    errorKey: 'credentials_cpf_or_cnpj_error',
    validators: [BRAZILIAN_TAX_ID_SHAPE_VALIDATOR],
    formatOnInput: formatCpfOrCnpjProgressively,
    resolve: (value: string) => {
      const digits = onlyDigits(value);
      const kind = classifyBrazilianTaxId(digits);
      const payload: Record<string, string> = {};

      if (kind === 'cpf') {
        payload['cpf'] = digits;
        return payload;
      }

      if (kind === 'cnpj') {
        payload['cnpj'] = digits;
        return payload;
      }

      return payload;
    },
  },
];

export function resolveCredentialSchema(
  schema: string[] | CredentialSchema,
): ResolvedCredentialSchema {
  if (Array.isArray(schema)) {
    return {
      baseRequiredSourceFields: [...schema],
      fields: buildResolvedFields(schema, new Set(schema)),
    };
  }

  const orderedSourceFields = getOrderedSourceFields(schema);
  const baseRequiredSourceFields = schema.required ?? [];
  const oneOfOptions = (schema.oneOf ?? [])
    .map((option) => option.required ?? [])
    .filter((option) => option.length > 0);
  const collapsibleGroup = findCollapsibleGroup(oneOfOptions);

  if (collapsibleGroup) {
    return {
      baseRequiredSourceFields,
      fields: buildCollapsedResolvedFields(
        orderedSourceFields,
        new Set(baseRequiredSourceFields),
        collapsibleGroup,
      ),
      oneOf: {
        type: 'collapsed',
        options: oneOfOptions,
        controlNames: [collapsibleGroup.controlName],
      },
    };
  }

  if (oneOfOptions.length > 0) {
    const requiredSourceFields = new Set([
      ...baseRequiredSourceFields,
      ...getIntersection(oneOfOptions),
    ]);

    return {
      baseRequiredSourceFields,
      fields: buildResolvedFields(orderedSourceFields, requiredSourceFields),
      oneOf: {
        type: 'separate',
        options: oneOfOptions,
        controlNames: flattenOptions(oneOfOptions),
      },
    };
  }

  return {
    baseRequiredSourceFields,
    fields: buildResolvedFields(
      orderedSourceFields,
      new Set(baseRequiredSourceFields),
    ),
  };
}

export function buildCredentialPayload(
  resolvedSchema: ResolvedCredentialSchema,
  values: Record<string, string>,
): Record<string, string> {
  if (!resolvedSchema.oneOf || resolvedSchema.oneOf.type === 'collapsed') {
    return buildFieldPayload(resolvedSchema.fields, values, () => true);
  }

  const matchedOption = findMatchingOneOfOption(
    values,
    resolvedSchema.oneOf.options,
  );
  const allowedSourceFields = new Set(resolvedSchema.baseRequiredSourceFields);

  matchedOption?.forEach((fieldName) => allowedSourceFields.add(fieldName));

  return buildFieldPayload(resolvedSchema.fields, values, (field) =>
    field.sourceFields.some((sourceField) =>
      allowedSourceFields.has(sourceField),
    ),
  );
}

export function getUnresolvedOneOfControlNames(
  resolvedSchema: ResolvedCredentialSchema,
  values: Record<string, string>,
): string[] | null {
  if (!resolvedSchema.oneOf || resolvedSchema.oneOf.type === 'collapsed') {
    return null;
  }

  const matchedOption = findMatchingOneOfOption(
    values,
    resolvedSchema.oneOf.options,
  );

  return matchedOption ? null : resolvedSchema.oneOf.controlNames;
}

function buildResolvedFields(
  orderedSourceFields: string[],
  requiredSourceFields: Set<string>,
): ResolvedCredentialField[] {
  return sortResolvedFields(
    orderedSourceFields.map((fieldName, index) =>
      createDefaultResolvedField(
        fieldName,
        index,
        requiredSourceFields.has(fieldName),
      ),
    ),
  );
}

function buildCollapsedResolvedFields(
  orderedSourceFields: string[],
  requiredSourceFields: Set<string>,
  collapsibleGroup: CollapsibleGroup,
): ResolvedCredentialField[] {
  const fields: ResolvedCredentialField[] = [];
  let collapsedFieldAdded = false;

  orderedSourceFields.forEach((fieldName, index) => {
    if (collapsibleGroup.members.includes(fieldName)) {
      if (!collapsedFieldAdded) {
        fields.push(createCollapsedResolvedField(collapsibleGroup, index));
        collapsedFieldAdded = true;
      }
      return;
    }

    fields.push(
      createDefaultResolvedField(
        fieldName,
        index,
        requiredSourceFields.has(fieldName),
      ),
    );
  });

  if (!collapsedFieldAdded) {
    fields.push(
      createCollapsedResolvedField(
        collapsibleGroup,
        orderedSourceFields.length,
      ),
    );
  }

  return sortResolvedFields(fields);
}

function createDefaultResolvedField(
  fieldName: string,
  order: number,
  required: boolean,
): ResolvedCredentialField {
  const semantic = inferCredentialSemantic(fieldName);
  const copy = DEFAULT_FIELD_COPY_BY_SEMANTIC[semantic];

  return {
    controlName: fieldName,
    sourceFields: [fieldName],
    semantic,
    required,
    inputType:
      semantic === 'password'
        ? 'password'
        : semantic === 'email'
          ? 'email'
          : 'text',
    labelKey: copy?.labelKey,
    placeholderKey: copy?.placeholderKey,
    errorKey: copy?.errorKey,
    order,
    validators: [],
    toPayload: (raw: string) => (raw ? { [fieldName]: raw } : {}),
  };
}

function createCollapsedResolvedField(
  collapsibleGroup: CollapsibleGroup,
  order: number,
): ResolvedCredentialField {
  return {
    controlName: collapsibleGroup.controlName,
    sourceFields: [...collapsibleGroup.members],
    semantic: collapsibleGroup.semantic,
    required: true,
    inputType: collapsibleGroup.inputType,
    labelKey: collapsibleGroup.labelKey,
    placeholderKey: collapsibleGroup.placeholderKey,
    errorKey: collapsibleGroup.errorKey,
    order,
    validators: collapsibleGroup.validators,
    formatOnInput: collapsibleGroup.formatOnInput,
    toPayload: collapsibleGroup.resolve,
  };
}

function sortResolvedFields(
  fields: ResolvedCredentialField[],
): ResolvedCredentialField[] {
  return [...fields].sort((left, right) => {
    const leftIsPassword = left.semantic === 'password';
    const rightIsPassword = right.semantic === 'password';

    if (leftIsPassword !== rightIsPassword) {
      return leftIsPassword ? 1 : -1;
    }

    return left.order - right.order;
  });
}

function buildFieldPayload(
  fields: ResolvedCredentialField[],
  values: Record<string, string>,
  shouldIncludeField: (field: ResolvedCredentialField) => boolean,
): Record<string, string> {
  return fields.reduce<Record<string, string>>((payload, field) => {
    if (!shouldIncludeField(field)) {
      return payload;
    }

    return {
      ...payload,
      ...field.toPayload(values[field.controlName] || ''),
    };
  }, {});
}

function getOrderedSourceFields(schema: CredentialSchema): string[] {
  const orderedFields: string[] = [];

  Object.keys(schema.properties ?? {}).forEach((fieldName) =>
    pushIfMissing(orderedFields, fieldName),
  );
  (schema.required ?? []).forEach((fieldName) =>
    pushIfMissing(orderedFields, fieldName),
  );
  (schema.oneOf ?? []).forEach((option) => {
    (option.required ?? []).forEach((fieldName) =>
      pushIfMissing(orderedFields, fieldName),
    );
  });

  return orderedFields;
}

function pushIfMissing(target: string[], value: string): void {
  if (!target.includes(value)) {
    target.push(value);
  }
}

function findCollapsibleGroup(
  oneOfOptions: string[][],
): CollapsibleGroup | null {
  if (!oneOfOptions.every((option) => option.length === 1)) {
    return null;
  }

  const alternativeMembers = oneOfOptions.map((option) =>
    option[0].toLowerCase(),
  );

  return (
    COLLAPSIBLE_GROUPS.find((group) => {
      if (group.members.length !== alternativeMembers.length) {
        return false;
      }

      return (
        group.members.every((member) => alternativeMembers.includes(member)) &&
        alternativeMembers.every((member) => group.members.includes(member))
      );
    }) ?? null
  );
}

function inferCredentialSemantic(fieldName: string): CredentialSemantic {
  const normalizedFieldName = fieldName.toLowerCase();

  if (
    PASSWORD_KEYWORDS.some((keyword) => normalizedFieldName.includes(keyword))
  ) {
    return 'password';
  }

  if (
    normalizedFieldName.includes('username') ||
    normalizedFieldName === 'user' ||
    normalizedFieldName.includes('usuario')
  ) {
    return 'username';
  }

  if (
    normalizedFieldName.includes('email') ||
    normalizedFieldName.includes('e-mail')
  ) {
    return 'email';
  }

  if (normalizedFieldName === 'cpf') {
    return 'cpf';
  }

  if (normalizedFieldName === 'cnpj') {
    return 'cnpj';
  }

  return 'text';
}

function getIntersection(options: string[][]): string[] {
  if (options.length === 0) {
    return [];
  }

  return options[0].filter((fieldName) =>
    options.every((option) => option.includes(fieldName)),
  );
}

function flattenOptions(options: string[][]): string[] {
  return options.reduce<string[]>((accumulator, option) => {
    option.forEach((fieldName) => pushIfMissing(accumulator, fieldName));
    return accumulator;
  }, []);
}

function findMatchingOneOfOption(
  values: Record<string, string>,
  options: string[][],
): string[] | null {
  return (
    options.find((option) =>
      option.every((fieldName) => (values[fieldName] || '').trim().length > 0),
    ) ?? null
  );
}
